import React, { useMemo, useState } from 'react';
import { useTheme, Tooltip, Box } from '@mui/material';

import ChordChartNodeTooltip from './ChordChartNodeTooltip';
import ChordChartRibbonTooltip, { ChordChartRibbonTooltipProps } from './ChordChartRibbonTooltip';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import WidgetLegend from 'components/widgetParts/WidgetLegend';
import { WidgetLegendSettingsDefaults } from 'components/settingsConfig/uiComponents/SettingsLegend';
import ChordChartContent from './ChordChartContent';
import { chordChartDefaultSettings, ChordChartSettingsStateType } from './ChordChartUtils';
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName } from 'system/widget/Widget';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';

export interface ChordNode {
  _count: number;
  _key: string;
  _label: string;
  chords: {
    _buckets: {
      _count: number;
      _key: string;
      _label: string;
    }[];
    _field_label: string;
    _field_id: string;
  };
}

function ChordChartWidget(props: WithQueryWidgetProps): React.JSX.Element {
  const theme = useTheme();

  const [hoveredNodeID, setHoveredNodeID] = useState<number | undefined>();
  const [hoveredConnection, setHoveredConnection] = useState<ChordChartRibbonTooltipProps | undefined>();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'chord-chart');
  const requestBody = originalRequest?.requestInit?.body || {};
  const widgetLabel = requestBody?.aggs?.outer?.field || "";

  // const aggs = props.widget?.request_info?.body?.aggs || {};
  // const widgetLabel = aggs.outer.field;
  const maxBuckets = props.widget?.data?.maxBuckets || 10;
  const colorArray = theme.colors.namedColors.chartColorArray;

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'chord-chart');
  const buckets = (apiResponseInfo?.responseBody?.outer?._buckets || []).slice(0, maxBuckets);

  // Build the buckets, they should be represented as a matrix of counts
  // e.g. [[0, 1, 2], [3, 0, 4], [5, 6, 0]]
  // where order in each row matches the order of the buckets
  const bucketData = useMemo(() => {
    const bucketData = buckets.map((bucket: any) => {
      const innerBuckets = bucket.chords._buckets;
      const innerBucketRow = buckets.map((innerBucket: any) => {
        const bucketRow = innerBuckets.find((innerBucketItem: any) => innerBucket._key === innerBucketItem._key);
        if (bucket._key === innerBucket._key) {
          return 0;
        }
        return bucketRow ? bucketRow._count : 0;
      }) || [];
      return innerBucketRow;
    });
    return bucketData;
  }, [buckets]);

  const tooltipContent = useMemo(() => {
    if (hoveredNodeID !== undefined && buckets[hoveredNodeID]) {
      return ( <ChordChartNodeTooltip node={buckets[hoveredNodeID]} nodeColor={colorArray[hoveredNodeID%10]} /> );
    }
    if (hoveredConnection !== undefined) {
      return <ChordChartRibbonTooltip {...hoveredConnection} />;
    }

    return undefined;
  }, [hoveredNodeID, hoveredConnection]);

  const currentState: ChordChartSettingsStateType = {...chordChartDefaultSettings, ...props.widget?.data?.settings};

  const align = currentState.legendAlign?.horizontal || WidgetLegendSettingsDefaults.legendAlign?.horizontal;
  const verticalAlign = currentState.legendAlign?.vertical || WidgetLegendSettingsDefaults.legendAlign?.vertical;

  return (
    <>
      {bucketData.length !== 0 &&
        <WidgetLayout
          header={(currentState.widgetTitle !== 'none') &&
            <WidgetTitle
              alignment={currentState.widgetTitleAlignment}
              customTitle={currentState.customWidgetTitle}
              defaultTitle={widgetLabel}
              settingsTitle={currentState.widgetTitle}
              widgetQueryTitle={apiResponseInfo?.queryDisplayTitle} />
          }
        >
          <WidgetLegend
            colors={colorArray}
            data={buckets}
            horizontalAlign={align}
            layout={currentState.legendLayout}
            show={currentState.legendShow}
            tooltip={(item, index) => {
              return <ChordChartNodeTooltip node={item} nodeColor={colorArray[index % 10]} />;
            }}
            valueGetter={(item) => item._label}
            verticalAlign={verticalAlign}
          >
            <Tooltip
              title={tooltipContent}
              open={Boolean(tooltipContent)}
              followCursor={true}>
              <Box
                sx={{
                  height: "100%",
                  width: "100%"
                }}>
                <ChordChartContent
                  matrix={bucketData}
                  ribbonColor={currentState.colorBy}
                  onHover={(index, ribbon) => {
                    if (ribbon) {
                      const sourceNode = buckets[ribbon.source.index];
                      const targetNode = buckets[ribbon.target.index];
                      const sharedConnectionCount = sourceNode.chords._buckets
                        .find((bucket: Record<string, any>) => bucket._key === targetNode._key);

                      setHoveredConnection({
                        sourceBucket: sourceNode,
                        targetBucket: targetNode,
                        sourceColor: colorArray[ribbon.source.index%10],
                        targetColor: colorArray[ribbon.target.index%10],
                        sharedConnectionCount: sharedConnectionCount._count
                      });
                    } else {
                      setHoveredNodeID(index);
                      setHoveredConnection(undefined);
                    }
                  }}
                />
              </Box>
            </Tooltip>
          </WidgetLegend>
        </WidgetLayout>
      }
      {bucketData.length === 0 &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </>
  );
}

export default withQueryWidget(ChordChartWidget, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
